import * as THREE from "three";
import type { Tag } from "../graffiti";
import type { PackState } from "../sim/backpack";
import type { SkaterEvent } from "../sim/state";
import { WASH_RADIUS } from "../sim/tuning";
import { disposeWorld } from "./level-meshes";
import { canvas2d, rng } from "./textures";

// The map's graffiti (#151): a spray-painted tag on the surface for each of
// the level's `graffiti`, drawn on its own little canvas (seeded, so it looks
// the same every load). Where the power washer's jet hits one, paint comes
// off the canvas there, so a sweep leaves a clean streak; the whole tag
// fades as the sim scrubs it (`PackState.scrub`) and is gone once it's
// clean. A tag the sim has back to unscrubbed (a fresh skater, a reloaded
// map) is drawn whole again. Without a canvas (tests) it's a flat colour.

/** The canvas's size, px. */
const PX = 128;
/** A clean tag takes this long to fade the rest of the way out (s). */
const FADE_TIME = 0.3;
/** The tag sits this far off the surface, against z-fighting (m). */
const LIFT = 0.015;

const INKS = ["#ff3b6b", "#2fd0ff", "#ffd23f", "#7bea4a", "#b36bff", "#ff8a2a"];

/** Draws tag `seed`: three or four fat letters in an outline, a highlight and a few drips. */
function drawTag(ctx: CanvasRenderingContext2D, seed: number): void {
  const r = rng(0x7a9 + seed * 7919);
  ctx.clearRect(0, 0, PX, PX);
  const ink = INKS[seed % INKS.length]!;
  const letters = 3 + Math.floor(r() * 2);
  const w = (PX * 0.8) / letters;
  const strokes: [number, number][][] = [];
  for (let k = 0; k < letters; k++) {
    const x0 = PX * 0.1 + k * w;
    const pts: [number, number][] = [];
    const n = 3 + Math.floor(r() * 2);
    for (let j = 0; j < n; j++) pts.push([x0 + r() * w, PX * (0.3 + r() * 0.4) + (k - letters / 2) * 3]);
    strokes.push(pts);
  }
  const path = (pts: [number, number][]) => {
    ctx.beginPath();
    pts.forEach(([x, y], j) => (j ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.stroke();
  };
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  // Drips under the letters.
  ctx.strokeStyle = ink;
  ctx.lineWidth = 4;
  for (let d = 0; d < 4; d++) {
    const x = PX * (0.15 + r() * 0.7);
    const y = PX * (0.55 + r() * 0.1);
    path([
      [x, y],
      [x, y + PX * (0.08 + r() * 0.2)],
    ]);
  }
  ctx.strokeStyle = "#141414";
  ctx.lineWidth = 30;
  strokes.forEach(path);
  ctx.strokeStyle = ink;
  ctx.lineWidth = 20;
  strokes.forEach(path);
  ctx.strokeStyle = "rgba(255,255,255,0.7)";
  ctx.lineWidth = 3;
  strokes.forEach((pts) => path(pts.map(([x, y]) => [x - 2, y - 3])));
}

interface TagMesh {
  mesh: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  ctx: CanvasRenderingContext2D | null;
  tex: THREE.CanvasTexture | null;
  /** Whether some paint has come off the canvas since it was drawn. */
  streaked: boolean;
  /** Seconds into fading out once clean, or -1 while it's still there. */
  fade: number;
}

export class GraffitiMeshes {
  readonly root = new THREE.Group();
  private tags: TagMesh[];
  private local = new THREE.Vector3();

  constructor(private level: readonly Tag[]) {
    this.root.name = "graffiti";
    this.tags = level.map((t, i) => this.build(t, i));
    this.root.add(...this.tags.map((t) => t.mesh));
    this.root.updateMatrixWorld(true);
  }

  private build(t: Tag, seed: number): TagMesh {
    const ctx = canvas2d(PX, PX);
    let tex: THREE.CanvasTexture | null = null;
    if (ctx) {
      drawTag(ctx, seed);
      tex = new THREE.CanvasTexture(ctx.canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
    }
    const mat = new THREE.MeshBasicMaterial({
      color: tex ? 0xffffff : INKS[seed % INKS.length]!,
      map: tex,
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(t.r * 2, t.r * 2), mat);
    mesh.position.set(t.x + t.nx * LIFT, t.y + t.ny * LIFT, t.z + t.nz * LIFT);
    // Upright on a wall; on the ground, its top toward -z.
    if (Math.abs(t.ny) > 0.9) mesh.up.set(0, 0, -1);
    mesh.lookAt(t.x + t.nx, t.y + t.ny, t.z + t.nz);
    return { mesh, mat, ctx, tex, streaked: false, fade: -1 };
  }

  /** Takes paint off where this frame's `wash` events hit, and fades or redraws each tag as the sim has it. */
  update(pack: PackState, events: readonly SkaterEvent[], dt: number): void {
    for (const e of events) {
      if (e.type !== "wash") continue;
      this.tags.forEach((t, i) => {
        if (pack.cleaned.has(i) || !t.ctx) return;
        const at = t.mesh.worldToLocal(this.local.set(e.hit.x, e.hit.y, e.hit.z));
        const size = this.level[i]!.r * 2;
        if (Math.abs(at.x) > size / 2 + WASH_RADIUS || Math.abs(at.y) > size / 2 + WASH_RADIUS) return;
        // A soft round bite, a little at a time, so a held jet clears a spot and a sweep a streak.
        const ctx = t.ctx;
        ctx.save();
        ctx.globalCompositeOperation = "destination-out";
        ctx.globalAlpha = 0.12;
        ctx.beginPath();
        ctx.arc((at.x / size + 0.5) * PX, (0.5 - at.y / size) * PX, ((WASH_RADIUS * 0.6) / size) * PX, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        t.streaked = true;
        t.tex!.needsUpdate = true;
      });
    }
    this.tags.forEach((t, i) => {
      const scrub = pack.scrub[i] ?? 0;
      if (pack.cleaned.has(i)) {
        if (t.fade < 0) t.fade = 0;
        t.fade = Math.min(FADE_TIME, t.fade + dt);
        t.mat.opacity = 0.4 * (1 - t.fade / FADE_TIME);
        t.mesh.visible = t.fade < FADE_TIME;
        return;
      }
      t.fade = -1;
      t.mesh.visible = true;
      t.mat.opacity = 1 - 0.6 * scrub;
      if (scrub === 0 && t.streaked && t.ctx) {
        drawTag(t.ctx, i);
        t.streaked = false;
        t.tex!.needsUpdate = true;
      }
    });
  }

  dispose(): void {
    for (const t of this.tags) t.tex?.dispose();
    disposeWorld(this.root);
  }
}
